'use client'

import { useState, useEffect } from 'react'
import { motion } from 'motion/react'
import Image from 'next/image'
import Container from '@/components/structure/Container'
import Title from '@/components/content/common/Title'
import Link from '@/components/ui/Link'
import ScrollView from '@/components/ui/ScrollView'
import { Icon } from '@/icons/Icon'
import { cn } from '@/lib/utils'
import type { Page } from '@/payload-types'

type OrderByProps = {
  orderBy: string
  setOrderByAction: (orderBy: string) => void
}

type RolesTemplate = Extract<NonNullable<Page['layout']>[number], { blockType: 'rolesTemplate' }>
type RoleProps = NonNullable<RolesTemplate['roles']>[number]

export default function RolesBlock({ roles }: RolesTemplate) {
  const hasRoles = roles && roles.length > 0

  const [orderBy, setOrderBy] = useState('position')
  const [sortedRoles, setSortedRoles] = useState<RoleProps[]>(hasRoles ? roles : [])

  useEffect(() => {
    if (!hasRoles) return
    const sorted = [...roles].sort((a, b) => {
      if (orderBy === 'location') {
        const getLocationPriority = (location: string) => {
          if (location.includes('US')) return 1
          if (location.includes('EU')) return 2
          return 3
        }

        const aPriority = getLocationPriority(a.location)
        const bPriority = getLocationPriority(b.location)

        if (aPriority !== bPriority) {
          return aPriority - bPriority
        }

        const aPlace = a.location.split(',')[0].trim()
        const bPlace = b.location.split(',')[0].trim()
        return aPlace.localeCompare(bPlace)
      }

      const aValue = a[orderBy as keyof RoleProps] ?? ''
      const bValue = b[orderBy as keyof RoleProps] ?? ''
      return String(aValue).localeCompare(String(bValue))
    })
    setSortedRoles(sorted)
  }, [orderBy, roles, hasRoles])

  if (!hasRoles) {
    return null
  }

  return (
    <Container
      id="roles"
      className="mx-auto mb-14 flex flex-col gap-8 md:mb-32 lg:flex-row"
      reduced
    >
      <Title
        title="<span>Join us.</span>"
        split="Help people choose better software."
        subtitle="Open positions"
        icon={<Icon name="circle-dotted-user" />}
        type="split"
        align="left"
        className="items-center text-center lg:items-start lg:pr-8 lg:text-left"
      />

      <div className="flex w-full flex-col gap-6">
        <OrderBy orderBy={orderBy} setOrderByAction={setOrderBy} />
        <div className="flex w-full flex-col divide-y divide-gray-200 rounded-3xl border border-gray-200 bg-gray-50">
          {sortedRoles.map((role, index) => (
            <Role role={role} key={index} />
          ))}
        </div>
      </div>
    </Container>
  )
}

export function Role({ role }: { role: RoleProps }) {
  const getLocationIcon = () => {
    if (role.location.includes('US')) return 'us'
    if (role.location.includes('EU')) return 'eu'
    return null
  }

  const locationIcon = getLocationIcon()

  const icon = locationIcon ? (
    <Image
      src={`/img/flags/${locationIcon}.svg`}
      alt={role.location}
      width={18}
      height={18}
      className="h-4.5 w-4.5 rounded-full"
    />
  ) : (
    <Icon name="earth" />
  )

  return (
    <Link
      href={role.url}
      target="_blank"
      className="group flex h-auto cursor-pointer flex-col justify-between gap-5 overflow-hidden px-5 py-5 xs:flex-row xs:items-center sm:h-16 sm:py-0"
    >
      <div className="flex w-full flex-col text-15 sm:w-fit sm:flex-row sm:items-center sm:gap-2">
        <span className="truncate font-medium text-gray-950 dark:text-white">{role.position}</span>
        <span className="text-gray-500 dark:text-white/60">{role.department}</span>
      </div>
      <div className="flex items-center gap-3 text-14">
        <div className="flex items-center gap-2.5 pr-2 sm:pl-2">
          {icon}
          <span className="whitespace-nowrap text-gray-500 dark:text-white/60">
            {role.location}
          </span>
        </div>

        <div className="h-3.5 w-px shrink-0 bg-gray-200 dark:bg-white/10"></div>
        <div className="flex items-center gap-2.5 whitespace-nowrap px-2 font-medium text-gray-950 transition-colors group-hover:text-primary-500 dark:text-white">
          Apply now
          <Icon name="arrow-up-right" size={12} />
        </div>
      </div>
    </Link>
  )
}

export function OrderBy({ orderBy, setOrderByAction }: OrderByProps) {
  const params = ['position', 'department', 'location']

  return (
    <div className="flex h-7 w-full items-center gap-6 px-4 text-14 text-gray-500">
      <span className="cursor-default whitespace-nowrap">Order by</span>
      <ScrollView className="flex h-7 w-full items-center gap-2">
        {params.map((param) => {
          const isActive = orderBy === param

          return (
            <button
              key={param}
              type="button"
              onClick={() => setOrderByAction(param)}
              aria-pressed={isActive}
              className="relative w-fit cursor-pointer font-medium"
            >
              <span
                className={cn(
                  'relative z-10 px-3 leading-none transition-colors delay-150',
                  isActive ? 'text-white' : 'text-gray-500 hover:text-gray-950',
                )}
              >
                {param.charAt(0).toUpperCase() + param.slice(1)}
              </span>
              {isActive && (
                <motion.div
                  layoutId="highlight"
                  className="absolute -top-0.5 left-0 z-0 h-7 w-full rounded-full bg-black"
                ></motion.div>
              )}
            </button>
          )
        })}
      </ScrollView>
    </div>
  )
}
